import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Button,
  TextField,
  Autocomplete,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { showError, showSuccess } from "../../../toastify";
import { useRequiredAuth } from "../../common/guard/requiredAuth";
import {
  normalizeClassGradeCodeInput,
  isValidCustomClassGradeCode,
  customClassGradeCodeHintShort,
  schoolLevelNameToGradeTier,
  classGradeMatchesSchoolLevelTier,
  schoolTierGradeHint,
} from "../utils/classGradeCodeInput";

const AssignSubjectsDialog = (props) => {
  const {
    disabled,
    showDialog,
    setShowDialog,
    schoolTeachers, // Pass the list of teachers for the dropdown
    setAsCPDMentor,
  } = props;

  const { hasPermissions } = useRequiredAuth();
  const canEditPlacementSubjectGrades = hasPermissions([
    "SCHOOL_MANAGEMENT_STAFF_ASSIGN_SUBJECTS",
  ]);

  const [loading, setLoading] = useState(false);
  const [selectedTargetTeacher, setSelectedTargetTeacher] = useState(null);

  /** Class-grade catalog + per-placement subject ↔ grades (multi) */
  const [classGradesCatalog, setClassGradesCatalog] = useState([]);
  /** Mirrors server placement subject↔grades; used when merging quick-add PUT */
  const [placementGradeRows, setPlacementGradeRows] = useState([]);

  // Assigned Subjects State
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [availableSchoolLevels, setAvailableSchoolLevels] = useState([]);
  const [availableSubLevels, setAvailableSubLevels] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedAssignedLevel, setSelectedAssignedLevel] = useState(null);
  const [selectedAssignedSubLevel, setSelectedAssignedSubLevel] =
    useState(null);
  const [selectedAssignedSubject, setSelectedAssignedSubject] = useState(null);
  /** Class grades picked with Level/Class/Subject before Add (paired with placement save) */
  const [quickAddSelectedGrades, setQuickAddSelectedGrades] = useState([]);

  const selectedLevelGradeTier = useMemo(
    () => schoolLevelNameToGradeTier(selectedAssignedLevel),
    [selectedAssignedLevel],
  );

  /** Catalog rows restricted to Primary / Senior families for the Level dropdown */
  const gradesForSelectedSchoolLevel = useMemo(() => {
    if (!selectedLevelGradeTier) return classGradesCatalog;
    return classGradesCatalog.filter((row) =>
      classGradeMatchesSchoolLevelTier(
        normalizeClassGradeCodeInput(row.code),
        selectedLevelGradeTier,
      ),
    );
  }, [classGradesCatalog, selectedLevelGradeTier]);

  const [mentorshipSubjects, setMentorshipSubjects] = useState([]);
  const [selectedMentorshipSubject, setSelectedMentorshipSubject] =
    useState(null);

  const [isCpdCoordinator, setIsCpdCoordinator] = useState(
    selectedTargetTeacher?.isCpdCoordinator || false,
  );
  const [isIEFT, setIsIEFT] = useState(selectedTargetTeacher?.isIEFT || false);
  const [isSSM, setIsSSM] = useState(
    !!selectedTargetTeacher?.mentorshipSubjectId || false,
  );

  useEffect(() => {
    setIsCpdCoordinator(selectedTargetTeacher?.isCpdCoordinator || false);
    setIsIEFT(selectedTargetTeacher?.isIEFT || false);
    setIsSSM(!!selectedTargetTeacher?.mentorshipSubjectId || false);
    setSelectedMentorshipSubject(
      mentorshipSubjects.find(
        (ms) => ms.id === selectedTargetTeacher?.mentorshipSubjectId,
      ) || null,
    );
  }, [selectedTargetTeacher, mentorshipSubjects]);

  /** Level always follows the teacher's placement teaching level — not user-editable. */
  useEffect(() => {
    if (!selectedTargetTeacher) {
      setSelectedAssignedLevel(null);
      setSelectedAssignedSubLevel(null);
      setSelectedAssignedSubject(null);
      setQuickAddSelectedGrades([]);
      setAvailableSubLevels([]);
      setAvailableSubjects([]);
      return;
    }
    setSelectedAssignedSubLevel(null);
    setSelectedAssignedSubject(null);
    setQuickAddSelectedGrades([]);
    setAvailableSubjects([]);
  }, [selectedTargetTeacher]);

  useEffect(() => {
    if (!selectedTargetTeacher?.teachingLevelId) {
      setSelectedAssignedLevel(null);
      return;
    }
    const level = availableSchoolLevels.find(
      ({ id }) => id === selectedTargetTeacher.teachingLevelId,
    );
    setSelectedAssignedLevel(level || null);
  }, [selectedTargetTeacher, availableSchoolLevels]);

  useEffect(async () => {
    if (!!selectedAssignedLevel) {
      try {
        const { data } = await axios.get(
          `/api/posts/school-sub-levels/${selectedAssignedLevel.id}`,
        );
        setAvailableSubLevels(data);

        if (data.length === 1) setSelectedAssignedSubLevel(data[0]);
      } catch (error) {}
    }
  }, [selectedAssignedLevel]);

  useEffect(async () => {
    if (!!selectedAssignedSubLevel) {
      try {
        const { data } = await axios.get(
          `/api/posts/sub-level-subjects/${selectedAssignedSubLevel.id}`,
        );
        setAvailableSubjects(data);
      } catch (error) {}
    }
  }, [selectedAssignedSubLevel]);

  // Close Dialog
  const onClose = () => {
    setShowDialog(false);
    setSelectedTargetTeacher(null);
    setTeacherSubjects([]);
    setPlacementGradeRows([]);
    setClassGradesCatalog([]);
    setQuickAddSelectedGrades([]);
  };

  // Fetch Teacher's Assigned Subjects
  const getAssignedSubjects = async () => {
    if (!selectedTargetTeacher || !selectedTargetTeacher.teacherPlacementId)
      return;
    setLoading(true);
    try {
      //   setTeacherSubjects([]);
      const { data } = await axios.get(
        `/api/posts/teacher-subjects/${selectedTargetTeacher.teacherPlacementId}`,
      );
      setTeacherSubjects(data);
    } catch (error) {
      showError("Failed to fetch assigned subjects");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Available School Levels (Initial Load)
  const getAvailableSchoolLevels = async () => {
    try {
      const { data } = await axios.get("/api/posts/school-levels");
      setAvailableSchoolLevels(data);
    } catch (e) {
      // Handle error silently or log
    }
  };

  const getMentorshipSubjects = async () => {
    try {
      const { data } = await axios.get("/api/cpd/mentorship-subjects");
      setMentorshipSubjects(data);
    } catch (e) {
      // Handle error silently or log
    }
  };

  const normalizeClassGrade = (g) => ({
    id: g?.id != null ? Number(g.id) || String(g.id) : g?.id,
    code: g?.code,
    name: g?.name,
    sortOrder: g?.sortOrder,
    scopeId: g?.scopeId,
  });

  const fetchClassGradeCatalogSubjectsAndPlacementGrades = async (
    teacher = selectedTargetTeacher,
  ) => {
    if (!teacher?.teacherPlacementId) {
      setPlacementGradeRows([]);
      setClassGradesCatalog([]);
      return;
    }

    try {
      const { data: grades } = await axios.get("/api/posts/class-grades");
      const catalog = (Array.isArray(grades) ? grades : [])
        .map(normalizeClassGrade)
        .filter((g) => g.code);
      setClassGradesCatalog(catalog);
    } catch (error) {
      showError(error);
      setClassGradesCatalog([]);
    }

    try {
      const { data } = await axios.get(
        `/api/posts/teacher-placement-subject-grades/${teacher.teacherPlacementId}`,
      );
      const list = Array.isArray(data) ? data : [];
      setPlacementGradeRows(
        list.map((g) => ({
          teacherSubjectId: g.teacherSubjectId,
          grades: (g.classGrades || []).map((cg) => ({
            id: cg.id,
            code: cg.code,
          })),
        })),
      );
    } catch (error) {
      setPlacementGradeRows([]);
    }
  };

  const mergeAssignmentsWithExistingRows = (
    rows,
    newTeacherSubjectId,
    newGradeNumericIds,
  ) => {
    const byTeacherSubjectId = new Map();
    for (const row of rows) {
      if (row.teacherSubjectId == null) continue;
      const tid = Number(row.teacherSubjectId);
      if (!Number.isInteger(tid) || tid <= 0) continue;
      const set = byTeacherSubjectId.get(tid) || new Set();
      for (const g of row.grades || []) {
        if (g?.id != null) {
          const n = Number(g.id);
          if (Number.isInteger(n) && n > 0) set.add(n);
        }
      }
      byTeacherSubjectId.set(tid, set);
    }
    const newTid = Number(newTeacherSubjectId);
    if (Number.isInteger(newTid) && newTid > 0) {
      const set = byTeacherSubjectId.get(newTid) || new Set();
      for (const id of newGradeNumericIds) {
        if (Number.isInteger(id) && id > 0) set.add(id);
      }
      byTeacherSubjectId.set(newTid, set);
    }
    return [...byTeacherSubjectId.entries()]
      .map(([teacherSubjectId, idSet]) => ({
        teacherSubjectId,
        classGradeIds: [...idSet],
      }))
      .filter((a) => a.classGradeIds.length > 0);
  };

  /** Match teacher-subject assignment row ↔ saved placement grades (by TeacherSubjects.id) */
  const displayGradesCellForTeacherSubject = (ts) => {
    if (ts?.id == null) return "—";

    const row = placementGradeRows.find(
      (r) => Number(r.teacherSubjectId) === Number(ts.id),
    );
    if (!row?.grades?.length) return "—";

    const codes = row.grades
      .map((g) => {
        if (g?.code) return String(g.code);
        const cat = classGradesCatalog.find(
          (c) => String(c.id) === String(g?.id),
        );
        return cat?.code || "";
      })
      .filter(Boolean)
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

    return codes.length ? codes.join(", ") : "—";
  };

  // Load levels when dialog opens
  useEffect(() => {
    if (showDialog) {
      getAvailableSchoolLevels();
      getMentorshipSubjects();
    }
  }, [showDialog]);

  // Load subjects when a teacher is selected
  useEffect(() => {
    if (selectedTargetTeacher) {
      getAssignedSubjects();
    } else {
      setTeacherSubjects([]);
    }
  }, [selectedTargetTeacher]);

  useEffect(() => {
    fetchClassGradeCatalogSubjectsAndPlacementGrades(
      selectedTargetTeacher || null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch grades when selected teacher identity changes
  }, [selectedTargetTeacher]);

  const handleSubLevelChange = async (event, subLevel) => {
    setSelectedAssignedSubLevel(subLevel);
    setSelectedAssignedSubject(null);
    setQuickAddSelectedGrades([]);
    setAvailableSubjects([]);
  };

  /** Resolves catalog options + validated free-text codes via POST class-grades/ensure */
  const resolveQuickAddGradesToNumericIds = async (selections) => {
    const tier = selectedLevelGradeTier;

    const idsFromCatalog = [];
    const customCodesRaw = [];

    for (const item of selections || []) {
      if (item == null) continue;
      if (typeof item === "string") {
        const n = normalizeClassGradeCodeInput(item);
        if (!n) continue;
        if (!isValidCustomClassGradeCode(n)) {
          showError(
            `Invalid grade code "${item.trim()}". ${customClassGradeCodeHintShort()}`,
          );
          return null;
        }
        if (!classGradeMatchesSchoolLevelTier(n, tier)) {
          showError(
            `"${item.trim()}" does not fit Level "${selectedAssignedLevel?.name || ""}". ${
              schoolTierGradeHint(tier) || ""
            }`.trim(),
          );
          return null;
        }
        customCodesRaw.push(n);
        continue;
      }
      const num = Number(item.id);
      const codeNorm = normalizeClassGradeCodeInput(item.code ?? "");
      if (
        codeNorm &&
        tier &&
        !classGradeMatchesSchoolLevelTier(codeNorm, tier)
      ) {
        showError(
          `Grade "${item.code}" does not fit Level "${selectedAssignedLevel?.name || ""}". ${
            schoolTierGradeHint(tier) || ""
          }`.trim(),
        );
        return null;
      }
      if (Number.isInteger(num) && num > 0) idsFromCatalog.push(num);
    }

    const uniqCustom = [...new Set(customCodesRaw)];
    let ensuredIds = [];
    if (uniqCustom.length > 0) {
      try {
        const { data } = await axios.post("/api/posts/class-grades/ensure", {
          codes: uniqCustom,
        });
        for (const row of data || []) {
          const n = Number(row.id);
          if (Number.isInteger(n) && n > 0) ensuredIds.push(n);
        }
      } catch (e) {
        showError(
          e.response?.data ||
            e.message ||
            "Could not validate or save custom grade codes.",
        );
        return null;
      }
    }

    const merged = [...new Set([...idsFromCatalog, ...ensuredIds])].filter(
      (id) => Number.isInteger(id) && id > 0,
    );
    return merged;
  };

  const handleAddSubject = async () => {
    if (!selectedAssignedSubject || !selectedTargetTeacher) return;

    const subjectId =
      selectedAssignedSubject.subjectId ??
      selectedAssignedSubject.subject?.id ??
      null;

    if (
      canEditPlacementSubjectGrades &&
      (!subjectId || quickAddSelectedGrades.length === 0)
    ) {
      showError(
        "Choose one or more class grades for this subject before assigning.",
      );
      return;
    }

    try {
      let newGradeIdsForPut = [];
      if (canEditPlacementSubjectGrades && subjectId) {
        const resolved = await resolveQuickAddGradesToNumericIds(
          quickAddSelectedGrades,
        );
        if (resolved === null) return;
        if (resolved.length === 0) {
          showError(
            "No valid grades to save — pick catalog rows or fix custom codes.",
          );
          return;
        }
        newGradeIdsForPut = resolved;
      }

      const { data: newAssignment } = await axios.post(
        "/api/posts/teacher-subjects",
        {
          teacherPlacementId: selectedTargetTeacher.teacherPlacementId,
          schoolSubLevelSubjectId: selectedAssignedSubject.id,
        },
      );

      if (
        canEditPlacementSubjectGrades &&
        subjectId &&
        newGradeIdsForPut.length &&
        newAssignment?.id != null
      ) {
        const assignments = mergeAssignmentsWithExistingRows(
          placementGradeRows,
          newAssignment.id,
          newGradeIdsForPut,
        );
        await axios.put("/api/posts/teacher-placement-subject-grades", {
          teacherPlacementId: selectedTargetTeacher.teacherPlacementId,
          assignments,
        });
      }

      getAssignedSubjects();
      await fetchClassGradeCatalogSubjectsAndPlacementGrades(
        selectedTargetTeacher,
      );
      setQuickAddSelectedGrades([]);
      setSelectedAssignedSubject(null);
      showSuccess("Subject assigned and grades saved.");
    } catch (error) {
      showError(error.response?.data || error || "Failed to assign subject");
    }
  };

  const handleDeleteSubject = async (id) => {
    try {
      await axios.delete(`/api/posts/teacher-subjects/${id}`);
      getAssignedSubjects();
      await fetchClassGradeCatalogSubjectsAndPlacementGrades(
        selectedTargetTeacher,
      );
    } catch (e) {
      showError("Failed to delete assigned subject");
    }
  };

  const onSave = () => {
    setAsCPDMentor({
      teacherPlacementId: selectedTargetTeacher?.teacherPlacementId,
      employeeId: selectedTargetTeacher?.id,
      isCpdCoordinator,
      isIEFT,
      mentorshipSubjectId: selectedMentorshipSubject?.id || null,
    });
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={showDialog}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle className="text-primary pb-0 mb-0 text-uppercase border">
        <span className="d-flex align-items-center mb-2">
          <Typography className="d-flex align-items-center text-primary">
            <span className="text-dark mr-1">Assign Subjects to Teacher</span>
          </Typography>
        </span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 4,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <span className="material-icons">close</span>
        </IconButton>
      </DialogTitle>

      <DialogContent className="px-4 py-4">
        <div className="row">
          {/* Select Teacher */}

          <div className="col-12 mb-3">
            <Autocomplete
              size="small"
              options={
                schoolTeachers.filter(
                  ({ isActingTeacher }) => !isActingTeacher,
                ) || []
              }
              getOptionLabel={(option) =>
                `${option.lastName} ${option.firstName} (${
                  option.phone || option.phoneNumber || "No Phone"
                })`
              }
              value={selectedTargetTeacher}
              onChange={(e, v) => setSelectedTargetTeacher(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Teacher to Assign"
                  size="small"
                  variant="outlined"
                />
              )}
            />
          </div>

          <div className="col-12">
            <fieldset className="row rounded bg-light form-group border align-items-end p-3 mx-0">
              <legend
                className="col-12 w-auto px-2 text-primary"
                style={{ fontSize: "0.9rem", fontWeight: 500 }}
              >
                Manage Subjects
              </legend>

              {/* Add New Subject Form */}
              <div className="col-12 mb-3">
                <div className="row">
                  <div className="col-12 mb-3">
                    <TextField
                      fullWidth
                      value={selectedTargetTeacher?.positionName || ""}
                      label="Teacher Position"
                      placeholder="Teacher Position"
                      size="small"
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                    />
                  </div>
                  <div className="col-md-2 col-sm-6 mb-2">
                    <TextField
                      size="small"
                      fullWidth
                      label="Level"
                      value={
                        selectedAssignedLevel?.name ||
                        selectedTargetTeacher?.teachingLevelName ||
                        ""
                      }
                      disabled={!selectedTargetTeacher}
                      InputProps={{ readOnly: true }}
                      helperText="From teacher placement"
                      title="Level is taken from the teacher's position / placement; it cannot be changed here."
                    />
                  </div>
                  <div className="col-md-2 col-sm-6 mb-2">
                    <Autocomplete
                      size="small"
                      options={availableSubLevels}
                      getOptionLabel={(option) => option.name}
                      value={selectedAssignedSubLevel}
                      onChange={handleSubLevelChange}
                      disabled={!selectedAssignedLevel}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Class/Sub-Level"
                          size="small"
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 mb-2">
                    <Autocomplete
                      size="small"
                      options={availableSubjects.filter(
                        ({ id }) =>
                          !teacherSubjects.find(
                            ({ schoolSubLevelSubject }) =>
                              id === schoolSubLevelSubject.id,
                          ),
                      )}
                      getOptionLabel={(option) => option.subject?.name || ""}
                      value={selectedAssignedSubject}
                      onChange={(e, v) => {
                        setSelectedAssignedSubject(v);
                        setQuickAddSelectedGrades([]);
                      }}
                      disabled={!selectedAssignedSubLevel}
                      renderInput={(params) => (
                        <TextField {...params} label="Subject" size="small" />
                      )}
                    />
                  </div>
                  {canEditPlacementSubjectGrades ? (
                    <div className="col-md-3 col-sm-6 mb-2">
                      <Autocomplete
                        multiple
                        freeSolo
                        filterSelectedOptions
                        size="small"
                        options={gradesForSelectedSchoolLevel}
                        value={quickAddSelectedGrades}
                        getOptionLabel={(g) =>
                          typeof g === "string"
                            ? g
                            : g?.code
                              ? `${g.code} — ${g.name || ""}`
                              : ""
                        }
                        isOptionEqualToValue={(a, b) => {
                          if (a === b) return true;
                          const codeA =
                            typeof a === "string"
                              ? normalizeClassGradeCodeInput(a)
                              : normalizeClassGradeCodeInput(a?.code ?? "");
                          const codeB =
                            typeof b === "string"
                              ? normalizeClassGradeCodeInput(b)
                              : normalizeClassGradeCodeInput(b?.code ?? "");
                          if (codeA && codeB && codeA === codeB) return true;
                          if (
                            typeof a === "object" &&
                            a != null &&
                            typeof b === "object" &&
                            b != null &&
                            a.id != null &&
                            b.id != null
                          ) {
                            return String(a.id) === String(b.id);
                          }
                          return false;
                        }}
                        disabled={
                          !selectedAssignedLevel ||
                          !selectedAssignedSubject ||
                          !selectedTargetTeacher ||
                          loading
                        }
                        noOptionsText={
                          gradesForSelectedSchoolLevel.length === 0 &&
                          selectedLevelGradeTier
                            ? "No catalog grades match this Level — adjust Level or enter a fitting code."
                            : "Pick from list or type a code — Enter to confirm."
                        }
                        ListboxProps={{ style: { maxHeight: 280 } }}
                        onChange={(e, v) => setQuickAddSelectedGrades(v || [])}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Class grades"
                            placeholder="Pick or type P1A-B, then Enter…"
                            size="small"
                            helperText={
                              canEditPlacementSubjectGrades
                                ? [
                                    schoolTierGradeHint(selectedLevelGradeTier),
                                    customClassGradeCodeHintShort(),
                                  ]
                                    .filter(Boolean)
                                    .join(" ")
                                : undefined
                            }
                          />
                        )}
                      />
                    </div>
                  ) : null}
                  <div
                    className={
                      canEditPlacementSubjectGrades
                        ? "col-md-2 col-sm-6 mb-2"
                        : "col-md-3 col-sm-6 mb-2"
                    }
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddSubject}
                      disabled={
                        loading ||
                        !selectedAssignedSubject ||
                        (canEditPlacementSubjectGrades &&
                          quickAddSelectedGrades.length === 0) ||
                        (canEditPlacementSubjectGrades && !selectedAssignedLevel)
                      }
                      fullWidth
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              {/* List of Assigned Subjects */}
              <div className="col-12">
                <table className="table table-sm table-bordered table-striped bg-white">
                  <thead className="thead-light">
                    <tr>
                      <th>Level</th>
                      <th>Class</th>
                      <th>Subject</th>
                      <th>Class grades</th>
                      <th style={{ width: "80px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teacherSubjects.map((ts) => (
                      <tr key={ts.id}>
                        <td>
                          {
                            ts.schoolSubLevelSubject?.schoolSubLevel
                              ?.schoolLevel?.name
                          }
                        </td>
                        <td>
                          {ts.schoolSubLevelSubject?.schoolSubLevel?.name}
                        </td>
                        <td>{ts.schoolSubLevelSubject?.subject?.name}</td>
                        <td>{displayGradesCellForTeacherSubject(ts)}</td>
                        <td className="text-center">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSubject(ts.id)}
                            title="Remove subject"
                          >
                            <span
                              className="material-icons text-danger"
                              style={{ fontSize: 18 }}
                            >
                              delete
                            </span>
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                    {teacherSubjects.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          {selectedTargetTeacher
                            ? "No subjects assigned yet."
                            : "Select a teacher to view assigned subjects."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="col-12">
                <div
                  className="px-4 py-2 "
                  style={{
                    backgroundColor: "#e9ecef",
                    border: "1px solid #cfd3d8",
                  }}
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          className="py-0"
                          disabled={
                            loading || isIEFT || isSSM || !selectedTargetTeacher
                          }
                          checked={isCpdCoordinator}
                          onChange={(event) =>
                            setIsCpdCoordinator(event.target.checked)
                          }
                        />
                      }
                      label="Is CPD Coordinator"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          className="py-0"
                          disabled={
                            loading ||
                            isCpdCoordinator ||
                            isSSM ||
                            !selectedTargetTeacher
                          }
                          checked={isIEFT}
                          onChange={(event) => setIsIEFT(event.target.checked)}
                        />
                      }
                      label="Is Inclusive Education Focal Teacher (IEFT)"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          className="py-0"
                          disabled={
                            loading ||
                            isCpdCoordinator ||
                            isIEFT ||
                            !selectedTargetTeacher
                          }
                          checked={isSSM}
                          onChange={(event) => {
                            setIsSSM(event.target.checked);
                            setSelectedMentorshipSubject(null);
                          }}
                        />
                      }
                      label="Is School Subject Mentor(SSM)"
                    />
                  </FormGroup>
                  {!!isSSM && (
                    <div className="mt-2">
                      <Autocomplete
                        size="small"
                        options={mentorshipSubjects}
                        getOptionLabel={(option) => option.name}
                        value={selectedMentorshipSubject}
                        onChange={(e, value) =>
                          setSelectedMentorshipSubject(value)
                        }
                        // disabled={!selectedTargetTeacher}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="School Mentor subjects"
                            size="small"
                          />
                        )}
                      />
                    </div>
                  )}
                  <Button
                    disabled={
                      disabled || (!!isSSM && !selectedMentorshipSubject)
                    }
                    onClick={onSave}
                    color="primary"
                    variant="contained"
                    className="px-5 mt-2"
                    size="small"
                  >
                    SAVE
                  </Button>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignSubjectsDialog;
